import { useState } from 'react';
import { NWClient } from '../../../client/NWClient';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../common/constants/routes';
import { fetchDocumentTemplates } from '../../../slices/document-templates/documentTemplatesSlice';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { LibraryItem } from '../components/library-list/types';
import { useTranslation } from 'react-i18next';
import { showMessage } from '../../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import {
  hideOverlay,
  showOverlay,
} from '../../../slices/show-progress-overlay/showProgressOverlay';
import { useErrorMessage } from '../../../common/hooks/useErrorMessage';

export const useLibraryFunctions = () => {
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [libraryItemForGenerator, setLibraryItemForGenerator] = useState<LibraryItem | null>(null);
  const [pendingDownload, setPendingDownload] = useState<boolean>(false);
  const [planUpgradingMessage, setPlanUpgradingMessage] = useState<boolean>(false);
  const [disclaimerMessage, setDisclaimerMessage] = useState<boolean>(false);
  const handleS3DownloadLink = (id: number, filename: string) => {
    setPendingDownload(true);
    NWClient.downloadLibraryDocument(id, filename)
      .catch((error) => {
        toast.error(getErrorMessage(error));
      })
      .finally(() => {
        setPendingDownload(false);
      });
  };

  const handleLibraryItemCopy = (data: any, id: number) => {
    dispatch(showOverlay());
    NWClient.get(`document-library-html`, id)
      .then((response: any) => {
        data.content = response.html;
        return NWClient.post('document-template', data, false);
      })
      .then(() => {
        navigate(`${ROUTES.DOCUMENT_LIBRARY}?libraryType=own`);
        toast.success(t('messages.templateCopiedSuccessfully'));
      })
      .catch((error) => {
        if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
        ) {
          setPlanUpgradingMessage(true);
        } else {
          toast.error(getErrorMessage(error));
        }
      })
      .finally(() => {
        dispatch(hideOverlay());
      });
  };

  const handleTemplateCopy = (data: any, id: number, category?: string) => {
    dispatch(showOverlay());
    NWClient.post('document-template', data, false)
      .then(() => {
        dispatch(fetchDocumentTemplates({ data: { user: id, category } }));
        toast.success(t('messages.templateCopiedSuccessfully'));
      })
      .catch((error) => {
        if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
        ) {
          dispatch(showMessage(t('messages.monthLimitReached')));
        } else {
          toast.error(getErrorMessage(error));
        }
      })
      .finally(() => {
        dispatch(hideOverlay());
      });
  };

  const navigateToTemplatePage = (category: string, id: number, editMode?: boolean) => {
    navigate(
      `${ROUTES.DOCUMENT_LIBRARY}/${category}/${id}?${editMode ? 'editMode=true' : 'templateViewEditMode=true'}`
    );
  };

  return {
    handleS3DownloadLink,
    handleLibraryItemCopy,
    planUpgradingMessage,
    setPlanUpgradingMessage,
    pendingDownload,
    navigateToTemplatePage,
    handleTemplateCopy,
    disclaimerMessage,
    setDisclaimerMessage,
    libraryItemForGenerator,
    setLibraryItemForGenerator,
  };
};
